import React, { useState, useRef, useEffect } from 'react';

import { ADD_ICON } from '../../../constants/images';
import { useHorizontalScroll } from '../../../hooks/useHorizontalScroll/useHorizontalScroll';
import type { IGenericCarousel, IGenericCarouselButtonData } from '../../../interfaces/genericCarousel';
import { ITabBarItems } from '../../../interfaces/tabbars';
import BdsTooltip from '../BdsTooltip/BdsTooltip';
import BdsButton from '../Buttons/BdsButton/BdsButton';
import BdsCardContainer from '../Cards/BdsCardContainer/BdsCardContainer';
import BdsDivider from '../Globals/BdsDivider/BdsDivider';
import TabBar from '../TabBars/TabBar/TabBar';
import SliderController from './sections/SliderController/SliderController';
import './GenericCarousel.scss';

const GenericCarousel = ({ sliders, topData, filters, buttonData, divider }: IGenericCarousel) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const slidersContainerRef = useRef<HTMLDivElement | null>(null);

  const [itemWidth, setItemWidth] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const [tabBarItems, setTabBarItems] = useState<ITabBarItems[]>([]);
  const [hasScroll, setHasScroll] = useState(false);

  const { dragProps, preventClick } = useHorizontalScroll<HTMLDivElement>();

  const gapSize = divider ? 108 : 32;

  useEffect(() => {
    if (filters?.showFilter && filters.items) {
      setTabBarItems(filters.items);
    }
  }, [filters]);

  useEffect(() => {
    const calculateDimensions = () => {
      if (slidersContainerRef.current) {
        const container = slidersContainerRef.current;
        const firstSlider = container.querySelector('.gcc-slider');

        if (firstSlider) {
          setItemWidth(firstSlider.clientWidth);
        }
      }
    };

    calculateDimensions();
    window.addEventListener('resize', calculateDimensions);

    return () => {
      window.removeEventListener('resize', calculateDimensions);
    };
  }, []);

  useEffect(() => {
    const calculateScroll = () => {
      if (slidersContainerRef.current) {
        const container = slidersContainerRef.current;
        const containerWidth = container.clientWidth;
        const totalWidth = sliders.reduce((acc: number) => {
          const sliderElement = container.querySelector('.gcc-slider');
          const sliderWidth = sliderElement ? sliderElement.clientWidth : 0;
          return acc + sliderWidth + gapSize;
        }, 0);
        setHasScroll(totalWidth > containerWidth);
      }
    };

    calculateScroll();
    window.addEventListener('resize', calculateScroll);

    return () => {
      window.removeEventListener('resize', calculateScroll);
    };
  }, [sliders]);

  const onMouseUp = () => {
    if (slidersContainerRef.current) {
      const scrollPosition = slidersContainerRef.current.scrollLeft;
      const index = Math.round(scrollPosition / (itemWidth + gapSize));
      if (index !== currentIndex) {
        setCurrentIndex(index);
      }
    }

    dragProps.onMouseUp();
  };

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
    scrollToIndex(index);
  };

  const handlePrevClick = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      scrollToIndex(newIndex);
    }
  };

  const handleNextClick = () => {
    if (currentIndex < sliders.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      scrollToIndex(newIndex);
    }
  };

  const scrollToIndex = (index: number) => {
    if (slidersContainerRef.current && itemWidth > 0) {
      const scrollPosition = index * (itemWidth + gapSize);
      slidersContainerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleButtonAction = () => {
    if (buttonData) {
      buttonData.action();
    }
  };

  const renderButton = (data: IGenericCarouselButtonData) => {
    return (
      <BdsButton
        iconLeft={data.icon ?? ADD_ICON}
        action={handleButtonAction}
        size="s"
        variant={data.variant ?? 'primary'}
        disabled={data.disabled}
      >
        {data.text}
      </BdsButton>
    );
  };

  const getButtonElement = () => {
    if (!buttonData) return null;
    const button = renderButton(buttonData);

    if (buttonData.tooltipMessage) {
      return (
        <BdsTooltip message={buttonData.tooltipMessage} position="left" variant="standard">
          {button}
        </BdsTooltip>
      );
    }

    return button;
  };

  const changeTab = (index: number) => {
    setTabIndex(index);
    if (filters?.items && filters.onChange) {
      filters.onChange(index);
    }
  };

  const tabBarParams = {
    items: tabBarItems,
    action: changeTab,
    value: tabIndex
  };

  return (
    <BdsCardContainer
      className="generic-carousel flex flex-col justify-start items-start"
      borderRadiusSize="radius-l"
      paddingSize="padding-xl"
    >
      <div className="gcc-top flex flex-row justify-between items-start w-full">
        <div className="gcc-texts-container flex flex-col justify-start items-start">
          {topData && (
            <div className="gcc-texts flex flex-col justify-start items-start">
              <h2 className="title">{topData.title}</h2>
              <p className="description">{topData.description}</p>
            </div>
          )}
        </div>
        {getButtonElement()}
      </div>
      {filters && tabBarParams.items && tabBarParams.items.length > 2 && (
        <div className="gcc-filters">
          <TabBar items={tabBarParams.items} action={tabBarParams.action} activeIndex={tabBarParams.value} />
        </div>
      )}
      <div className="gcc-sliders-container flex flex-col justify-start items-start w-full">
        <div
          className={`gcc-sliders-list flex flex-row justify-start items-center w-full ${divider && 'with-divider'}`}
          data-testid="gcc-sliders-list"
          {...dragProps}
          ref={(el) => {
            if (slidersContainerRef) slidersContainerRef.current = el;
            if (dragProps.ref) {
              if (typeof dragProps.ref === 'function') {
                (dragProps.ref as (instance: HTMLDivElement | null) => void)(el);
              } else {
                dragProps.ref.current = el;
              }
            }
          }}
          onMouseUp={onMouseUp}
          style={{ paddingRight: hasScroll ? `${itemWidth + gapSize}px` : 0 }}
        >
          {React.Children.map(sliders, (child, index) => {
            if (!React.isValidElement(child)) return child;
            const childProps = child.props as any;
            return (
              <>
                <div key={`slider-${child.key}`} className="gcc-slider">
                  {React.cloneElement(child, {
                    ...childProps,
                    onClick: (e: React.MouseEvent) => {
                      if (preventClick) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      if (childProps.onClick) {
                        childProps.onClick(e);
                      }
                    },
                    action: () => {
                      if (preventClick) {
                        return;
                      }
                      if (childProps.action) {
                        childProps.action();
                      }
                    }
                  })}
                </div>
                {divider && index < sliders.length - 1 && <BdsDivider size="s" color="subtle" orientation="vertical" />}
              </>
            );
          })}
        </div>
        {hasScroll && (
          <div className="dots flex flex-row justify-end w-full">
            <SliderController
              totalDots={sliders.length}
              currentIndex={currentIndex}
              onDotClick={handleDotClick}
              onPrevClick={handlePrevClick}
              onNextClick={handleNextClick}
            />
          </div>
        )}
      </div>
    </BdsCardContainer>
  );
};

export default GenericCarousel;
